
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {QBtn, QSelect} from "quasar"
import Axios, {AxiosError, AxiosResponse} from "axios"
import Message from "@/components/Message.vue"
import {UserRole} from "@/AuthenticationService"
import SelectDropDown from "@/components/SelectDropDown.vue"
import CloseButton from "@/components/CloseButton.vue"

export interface RegistrationRequest {
  email: string | null
  role: string | null
  contextId: string | null
  name: string | null
  oneCentral: boolean
}

export interface BrowserClientRegistrationRequest {
  username: string | null
  domain: string | null
  contextId: string | null
}

interface RegistrationContext {
  id: string
  name: string
}

export interface ActivationRequest {
  username: string
  active: boolean
}

@Component({
  components: {
    CloseButton,
    QBtn,
    SelectDropDown,
    Message,
    PageHeader,
    HelpIcon,
    QSelect,
  },
})
export default class AuthenticationView extends Vue {
  readonly visibleUserColumns = [
    "username",
    "emailOrDomain",
    "role",
    "name",
    "contextId",
    "active",
    "verified",
    "failedAuthenticationAttempts",
  ]

  readonly roles = Object.keys(UserRole)
    .filter((value: string) => !Number.isNaN(Number(value)))
    .map(key => UserRole[Number.parseInt(key)])

  readonly ocRoles = [
    UserRole[UserRole.AuthorizedRep],
    UserRole[UserRole.Staff],
    UserRole[UserRole.Admin],
  ]

  readonly nonUserRoles = [
    UserRole[UserRole.Application],
    UserRole[UserRole.DevOps],
    UserRole[UserRole.BrowserClient],
  ]

  // eslint-disable-next-line unicorn/consistent-function-scoping
  readonly userRoles = this.roles.filter(r => !this.nonUserRoles.includes(r))

  users: Array<User> = new Array<User>()
  registrationUser: RegistrationRequest | null = null
  registrationBc: BrowserClientRegistrationRequest | null = null
  contexts: Array<RegistrationContext> | null = null
  userContexts = new Array<RegistrationContext>()
  role: string | null = null

  message: string | null = null
  error = false

  dataInProgress = false
  showRegistration = false
  showRegistrationBc = false
  registrationUpdate = false
  registrationUpdateBc = false
  currentUserActive = false

  get isRegistrationDisabled(): boolean {
    return (
      this.registrationUser == null ||
      this.registrationUser.email == null ||
      this.registrationUser.role == null ||
      this.registrationUser.contextId == null
    )
  }

  get isRegistrationDisabledBc(): boolean {
    return (
      this.registrationBc == null ||
      this.registrationBc.username == null ||
      this.registrationBc.domain == null ||
      this.registrationBc.contextId == null
    )
  }

  async created() {
    await this.getUsers()
    await this.getContexts()
  }

  private async getUsers() {
    this.dataInProgress = true
    await Axios.get(`/api/authentication/users`)
      .then((response: AxiosResponse) => {
        this.message = null
        this.users = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  get filteredUsers(): Array<User> {
    return this.users!.filter(u => this.role === null || u.role === this.role)
  }

  get isOneCentralAvailable(): boolean {
    return !this.registrationUpdate && this.ocRoles.includes(this.registrationUser!.role!)
  }

  private async getContexts() {
    await Axios.get(`/api/authentication/browserClientContexts`).then((response: AxiosResponse) => {
      this.contexts = response.data
    })
  }

  private roleSelected(role: string) {
    this.registrationUser!.role = role
    this.registrationUser!.contextId = null
    void Axios.get(`/api/authentication/userContexts`, {params: {role}}).then(
      (response: AxiosResponse) => (this.userContexts = response.data)
    )
  }

  private showUpdateUser(user: User) {
    if (user.role === "BrowserClient") {
      this.showUpdateBc(user)
    } else {
      this.registrationUser = {
        email: user.username,
        role: null,
        contextId: null,
        name: user.name,
        oneCentral: this.ocRoles.includes(user.role),
      }
      this.roleSelected(user.role)
      this.registrationUser.contextId = user.contextId
      this.registrationUpdate = true
      this.showRegistration = true
    }
    this.currentUserActive = user.active
  }

  private showUpdateBc(user: User) {
    this.registrationBc = {
      username: user.username,
      domain: user.emailOrDomain,
      contextId: user.contextId,
    }
    this.registrationUpdateBc = true
    this.showRegistrationBc = true
  }

  private showRegisterUser() {
    this.registrationUser = {
      email: null,
      role: null,
      contextId: null,
      name: null,
      oneCentral: true,
    }
    this.roleSelected(UserRole[UserRole.AuthorizedRep])
    this.registrationUser.contextId = "2794072762594c2a89c9ae6c06c2c2df"
    this.registrationUpdate = false
    this.showRegistration = true
  }

  private showRegisterBc() {
    this.registrationBc = {
      username: null,
      domain: null,
      contextId: null,
    }
    this.registrationUpdateBc = false
    this.showRegistrationBc = true
  }

  private async registerUser() {
    this.showRegistration = false
    this.dataInProgress = true
    void (await Axios.post(`/api/authentication/register`, this.registrationUser)
      .then((response: AxiosResponse) => {
        this.error = false
        this.message = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.errors
      })
      .finally(() => {
        this.dataInProgress = false
      }))
  }

  private async registerBc() {
    this.showRegistrationBc = false
    this.dataInProgress = true
    void (await Axios.post(`/api/authentication/registerBrowserClient`, this.registrationBc)
      .then((response: AxiosResponse) => {
        this.error = false
        this.message = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.errors
      })
      .finally(() => {
        this.dataInProgress = false
      }))
  }

  private async userActivation() {
    this.showRegistration = false
    this.showRegistrationBc = false
    this.dataInProgress = true

    const request: ActivationRequest = {
      username: this.registrationUpdate
        ? this.registrationUser!.email!
        : this.registrationBc!.username!,
      active: !this.currentUserActive,
    }

    void (await Axios.patch(`/api/authentication/activation`, request)
      .then((response: AxiosResponse) => {
        this.error = false
        this.message = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.error = true
        this.message = reason.response?.data.errors
      })
      .finally(() => {
        this.dataInProgress = false
      }))
  }
}
